import { AppThunkAction } from '../reducers';

// services
import * as Services from '../../services/Contract.IAImport.Service';

// models
import {
	NewContractIAImportProcess,
	ContractIAImportProcess,
	ImportFeedbackRatingScaleItem,
	RateContractIAImportProcess,
	RateImportConflictError
} from '../../models/Entities/Contracts/ContractIAImportProcess';
import { ErrorType } from '../../models/HttpError';

// actions
import * as Actions from '../actions';

type KnownAction =
	| Actions.ContractIAImport.ContractIAImportCreateRequestAction
	| Actions.ContractIAImport.ContractIAImportCreateSuccessAction
	| Actions.ContractIAImport.ContractIAImportCreateFailureAction
	| Actions.ContractIAImport.ContractIAImportGetLastRequestAction
	| Actions.ContractIAImport.ContractIAImportGetLastSuccessAction
	| Actions.ContractIAImport.ContractIAImportGetLastFailureAction
	| Actions.ContractIAImport.ContractIAImportGetHistoryRequestAction
	| Actions.ContractIAImport.ContractIAImportGetHistorySuccessAction
	| Actions.ContractIAImport.ContractIAImportGetHistoryFailureAction
	| Actions.ContractIAImport.ContractIAImportGetRatingScaleRequestAction
	| Actions.ContractIAImport.ContractIAImportGetRatingScaleSuccessAction
	| Actions.ContractIAImport.ContractIAImportGetRatingScaleFailureAction
	| Actions.ContractIAImport.ContractIAImportRateRequestAction
	| Actions.ContractIAImport.ContractIAImportRateSuccessAction
	| Actions.ContractIAImport.ContractIAImportRateFailureAction;

const CreateImportContractProcess = (
	newProcess: NewContractIAImportProcess
): AppThunkAction<KnownAction> => async (dispatch, getState) => {
	const state = getState();
	const token: string | null = state?.oidc?.user?.access_token ? state.oidc.user.access_token : null;

	if (token === null) {
		dispatch({
			type: 'CONTRACT_IAIMPORT_CREATE_FAILURE',
			error: {
				ErrorCode: 401,
				ErrorMessage: 'Not authorized',
				Errors: []
			}
		});
		return;
	}

	dispatch({ type: 'CONTRACT_IAIMPORT_CREATE_REQUEST', newProcess });

	try {
		Services.CreateImportContractProcess(newProcess, token)
			.then((process: ContractIAImportProcess) => {
				dispatch({
					type: 'CONTRACT_IAIMPORT_CREATE_SUCCESS',
					process
				});
			})
			.catch((error: ErrorType) =>
				dispatch({
					type: 'CONTRACT_IAIMPORT_CREATE_FAILURE',
					error
				})
			);
	} catch (error) {
		console.log(error);
	}
};

const GetLastImportContractProcess = (
	contractId: number
): AppThunkAction<KnownAction> => async (dispatch, getState) => {
	const state = getState();
	const token: string | null = state?.oidc?.user?.access_token ? state.oidc.user.access_token : null;

	if (token === null) {
		dispatch({
			type: 'CONTRACT_IAIMPORT_GETLAST_FAILURE',
			contractId,
			error: {
				ErrorCode: 401,
				ErrorMessage: 'Not authorized',
				Errors: []
			}
		});
		return;
	}

	dispatch({ type: 'CONTRACT_IAIMPORT_GETLAST_REQUEST', contractId });

	try {
		Services.GetLastImportContractProcess(contractId, token)
			.then((process: ContractIAImportProcess | null) => {
				dispatch({
					type: 'CONTRACT_IAIMPORT_GETLAST_SUCCESS',
					contractId,
					process: process ?? null
				});
			})
			.catch((error: ErrorType) =>
				dispatch({
					type: 'CONTRACT_IAIMPORT_GETLAST_FAILURE',
					contractId,
					error
				})
			);
	} catch (error) {
		console.log(error);
	}
};

const GetImportContractProcessHistory = (
	contractId: number
): AppThunkAction<KnownAction> => async (dispatch, getState) => {
	const state = getState();
	const token: string | null = state?.oidc?.user?.access_token ? state.oidc.user.access_token : null;

	if (token === null) {
		dispatch({
			type: 'CONTRACT_IAIMPORT_GETHISTORY_FAILURE',
			contractId,
			error: {
				ErrorCode: 401,
				ErrorMessage: 'Not authorized',
				Errors: []
			}
		});
		return;
	}

	dispatch({ type: 'CONTRACT_IAIMPORT_GETHISTORY_REQUEST', contractId });

	try {
		Services.GetImportContractProcessHistory(contractId, token)
			.then((processes: ContractIAImportProcess[]) => {
				dispatch({
					type: 'CONTRACT_IAIMPORT_GETHISTORY_SUCCESS',
					contractId,
					processes: processes || []
				});
			})
			.catch((error: ErrorType) =>
				dispatch({
					type: 'CONTRACT_IAIMPORT_GETHISTORY_FAILURE',
					contractId,
					error
				})
			);
	} catch (error) {
		console.log(error);
	}
};


const GetImportFeedbackRatingScale = (): AppThunkAction<KnownAction> => async (dispatch, getState) => {
	const state = getState();
	const token: string | null = state?.oidc?.user?.access_token ? state.oidc.user.access_token : null;

	if (token === null) {
		dispatch({
			type: 'CONTRACT_IAIMPORT_GETRATINGSCALE_FAILURE',
			error: {
				ErrorCode: 401,
				ErrorMessage: 'Not authorized',
				Errors: []
			}
		});
		return;
	}

	dispatch({ type: 'CONTRACT_IAIMPORT_GETRATINGSCALE_REQUEST' });

	try {
		const scale: ImportFeedbackRatingScaleItem[] = await Services.GetImportFeedbackRatingScale(token);
		dispatch({ type: 'CONTRACT_IAIMPORT_GETRATINGSCALE_SUCCESS', scale: scale || [] });
	} catch (error) {
		dispatch({ type: 'CONTRACT_IAIMPORT_GETRATINGSCALE_FAILURE', error: error as ErrorType });
	}
};

// El feedback se registra una única vez y es inmutable. El 409 no es un error
// de red a reintentar: trae la calificación vigente, que se propaga en la
// acción de fallo para que la UI muestre la puntuación que ya existe.
const RateImportContractProcess = (
	command: RateContractIAImportProcess
): AppThunkAction<KnownAction> => async (dispatch, getState) => {
	const state = getState();
	const token: string | null = state?.oidc?.user?.access_token ? state.oidc.user.access_token : null;

	if (token === null) {
		dispatch({
			type: 'CONTRACT_IAIMPORT_RATE_FAILURE',
			processId: command.processId,
			error: {
				ErrorCode: 401,
				ErrorMessage: 'Not authorized',
				Errors: []
			}
		});
		return;
	}

	dispatch({ type: 'CONTRACT_IAIMPORT_RATE_REQUEST', processId: command.processId });

	try {
		const process: ContractIAImportProcess = await Services.RateImportContractProcess(command, token);
		dispatch({
			type: 'CONTRACT_IAIMPORT_RATE_SUCCESS',
			processId: command.processId,
			process
		});
	} catch (error) {
		const conflict = error as RateImportConflictError;
		dispatch({
			type: 'CONTRACT_IAIMPORT_RATE_FAILURE',
			processId: command.processId,
			error: error as ErrorType,
			alreadyRatedProcess: conflict?.alreadyRatedProcess
		});
	}
};

export const actionCreators = {
	CreateImportContractProcess,
	GetLastImportContractProcess,
	GetImportContractProcessHistory,
	GetImportFeedbackRatingScale,
	RateImportContractProcess,
};

